import React from 'react';
import { createUseStyles, useTheme } from 'react-jss';
import { useT } from '../../i18n';

const MONO = 'ui-monospace, SFMono-Regular, Menlo, monospace';

const useStyles = createUseStyles({
  container: {
    padding: '14px',
    background: ({ theme }) => theme.colorPanel,
    borderRadius: '10px',
    border: '1px solid #4a3020',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.06)',
    display: 'flex',
    flexDirection: 'column',
    gap: '9px',
  },
  label: {
    fontFamily: MONO,
    color: '#c4a070',
    fontSize: '10px',
    fontWeight: '700',
    letterSpacing: '2px',
    textTransform: 'uppercase',
  },
  status: {
    color: ({ theme }) => theme.colorText,
    fontSize: '15px',
    lineHeight: 1.45,
  },
  // Same stamped gauge as BiddingPanel's — the two phases should read as one clock.
  timerPlate: {
    borderRadius: '10px',
    background: '#150d07',
    border: '1px solid #4a3020',
    padding: '10px',
    textAlign: 'center',
    boxShadow: 'inset 0 2px 6px rgba(0,0,0,0.6)',
  },
  timer: {
    fontFamily: MONO,
    fontSize: '30px',
    fontWeight: '700',
    lineHeight: 1,
    letterSpacing: '2px',
    fontVariantNumeric: 'tabular-nums',
    color: ({ theme, timeUrgent }) => timeUrgent ? '#e35c36' : theme.colorText,
  },
  timerCaption: {
    marginTop: '4px',
    fontFamily: MONO,
    fontSize: '9.5px',
    letterSpacing: '2px',
    textTransform: 'uppercase',
    color: ({ timeUrgent }) => timeUrgent ? '#d94f2b' : '#9c8168',
  },
  // Move budget as pips — the demoing player sees it draining without reading a number.
  pips: {
    display: 'flex',
    gap: '5px',
  },
  pip: {
    flex: 1,
    height: '7px',
    borderRadius: '4px',
  },
  pipFull: {
    background: '#d94f2b',
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.3)',
  },
  pipSpent: {
    background: '#150d07',
    boxShadow: 'inset 0 1px 3px rgba(0,0,0,0.6)',
  },
  moves: {
    fontFamily: MONO,
    fontSize: '11px',
    letterSpacing: '1.5px',
    textTransform: 'uppercase',
    color: ({ urgent }) => urgent ? '#e35c36' : '#d99a6a',
  },
  controls: {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  undoBtn: {
    width: '38px',
    height: '36px',
    flexShrink: 0,
    background: '#3a2718',
    border: '1px solid #533620',
    borderRadius: '7px',
    color: '#e8d6ae',
    cursor: 'pointer',
    fontSize: '15px',
    fontFamily: 'inherit',
    lineHeight: 1,
    boxShadow: 'inset 0 1px 0 rgba(255,255,255,0.1), inset 0 -2px 0 rgba(0,0,0,0.3)',
    '&:hover:not(:disabled)': {
      background: '#4a3220',
    },
    '&:active:not(:disabled)': {
      boxShadow: 'inset 0 2px 4px rgba(0,0,0,0.5)',
    },
  },
  giveUpBtn: {
    flex: 1,
    height: '36px',
    padding: '0 12px',
    border: '1px solid #6b4622',
    borderRadius: '7px',
    background: 'none',
    color: '#d99a6a',
    cursor: 'pointer',
    fontSize: '13px',
    fontWeight: '700',
    fontFamily: 'inherit',
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    transition: 'border-color .15s ease, color .15s ease',
    '&:hover': {
      borderColor: '#d94f2b',
      color: '#fff3e6',
    },
  },
});

/**
 * `movesAllowed` is optional — pass it from Game.js (the value already arrives on
 * PHASE_CHANGED) to get the pip row. Without it the component falls back to text.
 * `timerSeconds` is the turn clock from DEMO_TIMER_TICK; null until the first tick
 * lands, in which case no gauge is drawn.
 * `onUndo`/`onGiveUp` are only rendered for the demonstrating player.
 */
function DemoBar({ demoPlayerId, players, movesRemaining, movesAllowed, timerSeconds, isSelfDemo, onUndo, onGiveUp }) {
  const theme = useTheme();
  const urgent = movesRemaining <= 1;
  // 10s, not BiddingPanel's 15 — a hardcore demo turn is only 15s long, so a
  // 15s threshold would paint the whole turn red.
  const showTimer = timerSeconds !== null && timerSeconds !== undefined;
  const timeUrgent = showTimer && timerSeconds <= 10;
  const classes = useStyles({ theme, urgent, timeUrgent });
  const t = useT();
  const mmss = (s) => `${Math.floor(s / 60)}:${String(s % 60).padStart(2, '0')}`;

  const demoPlayer = players.find(p => p.id === demoPlayerId);
  // Which key, not which words — "You are demonstrating" is a fact about the
  // reader, and the whole sentence changes shape between languages.
  const demoStatus = isSelfDemo
    ? t('demo.statusSelf')
    : t('demo.statusOther', { name: demoPlayer?.username ?? t('log.someone') });
  const total = Number.isFinite(movesAllowed) && movesAllowed > 0 ? movesAllowed : null;
  // Running out of moves is not itself the end of the turn — the clock above is.
  // Point at the two things still worth doing instead of a bare "0 moves left".
  const movesLabel = movesRemaining === 0
    ? t('demo.outOfMoves')
    : total
      ? t('demo.movesOfTotal', { count: movesRemaining, total })
      : t('demo.movesLeft', { count: movesRemaining });

  return (
    <div className={classes.container}>
      <div className={classes.label}>{t('demo.title')}</div>
      <div className={classes.status}>{demoStatus}</div>

      {showTimer && (
        <div className={classes.timerPlate}>
          <div className={classes.timer}>{mmss(timerSeconds)}</div>
          <div className={classes.timerCaption}>
            {timeUrgent ? t('demo.timeRunningOut') : t('demo.timeToSolve')}
          </div>
        </div>
      )}

      {total && total <= 12 && (
        <div className={classes.pips}>
          {Array.from({ length: total }).map((_, i) => (
            <div
              key={i}
              className={`${classes.pip} ${i < movesRemaining ? classes.pipFull : classes.pipSpent}`}
            />
          ))}
        </div>
      )}

      <div className={classes.moves}>{movesLabel}</div>

      {isSelfDemo && onUndo && (
        <div className={classes.controls}>
          <button className={classes.undoBtn} onClick={onUndo} aria-label={t('demo.undo')}>
            ↩
          </button>
          {onGiveUp && (
            <button className={classes.giveUpBtn} onClick={onGiveUp}>
              {t('demo.giveUp')}
            </button>
          )}
        </div>
      )}
    </div>
  );
}

export default DemoBar;
